import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import moment from 'moment';
import { useBreadcrumbs } from '../../contexts/breadcrumbs-provider';
import Avatar from '../../components/common/avatar';
import Heading from '../../components/layout/coach/heading';
import { getApiRequest } from '../../utils/request';
import { daysOfWeekOptions, getCurrentWeekForJourney } from '../../utils/helpers';
import Loadable from '../../components/data/loadable';
import AdminTable from '../../components/common/admin-table';
import AutoForm from '../../components/form/auto-form';
import Frame from '../../components/layout/frame';
import ActionLinks from '../../components/data/action-links';


function JourneyCell({ checkinDay, checkinTime, lastMessageTime, startDate, week }) {
  const checkin = !!checkinDay && !!checkinTime ? `${checkinDay} at ${checkinTime}` : 'Not Set';
  const lastMessage = lastMessageTime ? moment.unix(lastMessageTime._seconds).format('lll') : 'Never';
  
  return (
    <>
      <span className="font-bold">Check In Time: </span>{` ${checkin}`}<br />
      <span className="font-bold">Last Message: </span>{` ${lastMessage}`}<br />
      <span className="font-bold">Week: </span>{` ${week}`}
    </>
  );
}



function formatRow(item) {
  const { _id: id, uid, lastMessageTime, startDate, checkinDay, checkinTime, toolkitName, profile } = item;
  const { photo, name, pronouns } = profile;
  const week = getCurrentWeekForJourney(item).weeks;
  return {
    name: (
      <div className="flex">
        <Avatar photo={photo} name={name} bg="bg-secondary-500" />
        <div className="pl-3">
          <Link to={`/coach/member/journey/${id}`} className="font-bold text-primary-500 text-lg">{name}</Link>
          {!!pronouns && <div>{pronouns}</div>}
        </div>
      </div>
    ),
    nameRaw: name,
    week,
    toolkit: toolkitName || 'Chill',
    journey: <JourneyCell week={week} checkinDay={checkinDay} checkinTime={checkinTime} lastMessageTime={lastMessageTime} startDate={startDate} />,
    // checkin: !!checkinDay && !!checkinTime ? <span>{checkinDay} at {checkinTime}</span> : 'Not Set',
    // lastMessageSent: lastMessageTime ? moment.unix(lastMessageTime._seconds).format('lll') : 'Never',
    // week: `Week ${getCurrentWeekBasedOnStartDate(startDate).weeks}`,
    id,
    actionLinks: [
      // {
      //   label: 'View',
      //   href: `/coach/member/journey/${id}`,
      // },
      {
        label: 'Chat',
        href: `/coach/member/${uid}/chat`,
      },
    ],
  };
}

export default function CoachMembers() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryParamsString, setQueryParamsString] = useState(null);
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([
      {
        href: '/coach/members',
        label: 'Your Members',
      },
    ]);
  }, []);

  const query = queryString.parse(window.location.search);
  const currentQueryParamsString = queryString.stringify(query || {});

  const load = async () => {
    if (!loading) {
      setLoading(true);
    }
    const result = await getApiRequest(`/coach/members${queryParamsString ? `?${queryParamsString}` : ''}`);
    // console.log(result.data, 'data');
    const encounteredMembers = [];
    if (result && result.data && result.data.length) {
      const sorted = result.data.sort((a, b) => b?.createdAt?._seconds - a?.createdAt?._seconds).filter(item => {
        const uid = item.uid;
        if (encounteredMembers.indexOf(uid) !== -1) {
          return false;
        }
        encounteredMembers.push(uid);
        return true;
      });
      setResults({
        ...result,
        data: sorted,
      });
    } else {
      setResults(result);
    }
    // setProfiles(result.data);
    setLoading(false);
  };

  useEffect(() => {
    if (currentQueryParamsString !== queryParamsString) {
      setQueryParamsString(currentQueryParamsString);
    }
  }, [currentQueryParamsString]);


  useEffect(() => {
    if (queryParamsString !== null && queryParamsString === currentQueryParamsString) {
      load();
    }
  }, [queryParamsString]);

  const columns = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'toolkit',
      label: 'Toolkit',
    },
    {
      key: 'journey',
      label: 'Journey Info',
    },
    // {
    //   key: 'week',
    //   label: 'Week',
    // },
    // {
    //   key: 'checkin',
    //   label: 'Check In Time',
    // },
    // {
    //   key: 'lastMessageSent',
    //   label: 'Last Message',
    // },
    {
      key: 'actions',
      label: '',
      isRight: true,
      render: (row) => { return !!row.actionLinks ? <ActionLinks links={row.actionLinks} /> : null; },
    },
  ];


  const baseUrl = '/coach/members';

  const clientSortOptions = [
    {
      label: 'Default Sort',
      comparison: () => load(),
    },
    {
      label: 'Sort By Week (Ascending)',
      comparison: (rows) => [...rows].sort((a, b) => a.week - b.week),
    },
    {
      label: 'Sort By Week (Descending)',
      comparison: (rows) => [...rows].sort((a, b) => b.week - a.week),
    },
    {
      label: 'Sort By Name',
      comparison: (rows) => [...rows].sort((a, b) => a.nameRaw < b.nameRaw ? -1 : a.nameRaw > b.nameRaw ? 1 : 0),
    },
  ];

  const searchForm = {
    type: 'get',
    noContainer: true,
    successRedirect: '/coach/members',
    fields: [
      {
        name: 'toolkitId',
        label: 'Toolkit',
        type: 'select',
        optionsUrl: '/options/toolkits',
      },
      {
        name: 'uid',
        label: 'Choose Member',
        type: 'select',
        optionsUrl: '/options/members',
      },
      {
        name: 'checkinDay',
        label: 'Check In Day',
        type: 'select',
        options: daysOfWeekOptions(),
      },
      {
        name: 'complete',
        label: 'Membership Status',
        type: 'select',
        options: [
          {
            value: '',
            label: 'Select...',
          },
          {
            value: '0',
            label: 'Active',
          },
          {
            value: '1',
            label: 'Completed (inactive)',
          }
        ],
      },
    ],
    submitLabel: 'Search Members',
  };

  return (
      <Frame fullscreen classes="p-4">        
        <Heading title="Your Members" />
        <Loadable loading={loading}>
          <AdminTable alternate results={results} columns={columns} clientSortOptions={clientSortOptions} baseUrl={baseUrl} rowFormatter={formatRow} searchTitle="Search Members" searchForm={<AutoForm {...searchForm} />} />
        </Loadable>
      </Frame>
  );
}
